/**
 * The skills panel's rules, extracted so they can be tested without rendering.
 *
 * The panel used to iterate the `CHARACTER` frame's `skills` array, which the
 * server deliberately trims: it sends state only for skills the player has
 * learned or *could* learn right now. That is the right thing for the frame —
 * repeating a hundred definitions on every equipment change would be enormous —
 * but it meant a fresh character opened the panel and saw nothing at all, with no
 * sense of what the hundred skills even are.
 *
 * So the panel reads the **full catalogue** from `WELCOME`, which every client
 * already has, and merges the per-skill state onto it where there is any. No
 * protocol change; the data was on the client the whole time.
 */

import type {
  Attributes,
  RefusalReason,
  SkillInfo,
  SkillState,
  WeaponClass,
} from '../net/protocol';

/** What the panel needs to know about one skill. */
export interface SkillRow {
  def: SkillInfo;
  /** Server state, when the frame carried any for this skill. */
  state: SkillState | undefined;
  learned: boolean;
  /** Learned, and its gate still met. */
  usable: boolean;
  /** The player's attributes already clear its gate. */
  gateMet: boolean;
  /** Which attributes fall short, and by how much. Empty when the gate is met. */
  missing: readonly GateShortfall[];
  /** Action-bar index, or -1. */
  slot: number;
  /** Locked = not learned and out of reach. Greyed, but visible and readable. */
  locked: boolean;
  /** Learnable right now: gate met, not learned, and a point to spend. */
  learnable: boolean;
  /** Points in this skill: 0 unlearned, 1 once learned. */
  rank: number;
  /**
   * How far it goes. **Defaults to 1**, which is both the honest state for the
   * fourteen skills nothing reads and the conservative fallback for a server that
   * does not send the field — either way the panel offers no button rather than one
   * that would be refused.
   */
  maxRank: number;
  /** A rank could be bought right now. */
  levellable: boolean;
  /**
   * Why it could not, when it cannot. The server's own key, so the greyed button and
   * the answer to a click on it agree.
   */
  levelBlocked: RefusalReason | undefined;
}

export interface GateShortfall {
  attribute: 'STR' | 'DEX' | 'INT' | 'END';
  need: number;
  have: number;
}

/** The four gates in a fixed order, so a tooltip always reads the same way. */
const GATES = [
  { key: 'str', label: 'STR' },
  { key: 'dex', label: 'DEX' },
  { key: 'int', label: 'INT' },
  { key: 'end', label: 'END' },
] as const;

/** Which gates a set of attributes falls short of. */
export function shortfalls(gates: Attributes, total: Attributes | null): GateShortfall[] {
  if (!total) return [];
  const out: GateShortfall[] = [];
  for (const { key, label } of GATES) {
    const need = gates[key];
    if (need > 0 && total[key] < need) {
      out.push({ attribute: label, need, have: total[key] });
    }
  }
  return out;
}

/**
 * Builds a row per skill in the catalogue, in tier then category order.
 *
 * Sorted so the panel reads as a progression rather than as the file's order:
 * everything reachable first within a tier, then the rest.
 */
export function skillRows(
  catalogue: readonly SkillInfo[],
  states: readonly SkillState[],
  total: Attributes | null,
  slotted: readonly string[],
  skillPoints: number,
): SkillRow[] {
  const byId = new Map(states.map((s) => [s.skillId, s]));

  const rows = catalogue.map((def) => {
    const state = byId.get(def.skillId);
    const missing = shortfalls(def.statGates, total);
    const gateMet = total !== null && missing.length === 0;
    const learned = state?.learned ?? false;
    const rank = state?.rank ?? (learned ? 1 : 0);
    const maxRank = def.maxRank ?? 1;

    // The server's own reason where it sent one, and the same answer derived where
    // it did not — a skill absent from the trimmed CHARACTER frame is one that is
    // neither learned nor learnable, which is `NOT_LEARNED` either way.
    const levelBlocked: RefusalReason | undefined =
      state?.blocked ??
      (rank === 0
        ? 'NOT_LEARNED'
        : rank >= maxRank
          ? 'MAX_RANK'
          : !gateMet
            ? 'GATE'
            : skillPoints <= 0
              ? 'NO_POINTS'
              : undefined);

    return {
      def,
      state,
      learned,
      usable: state?.usable ?? false,
      gateMet,
      missing,
      slot: slotted.indexOf(def.skillId),
      locked: !learned && !gateMet,
      learnable: !learned && gateMet && skillPoints > 0,
      rank,
      maxRank,
      levellable: levelBlocked === undefined,
      levelBlocked,
    };
  });

  // Learned first, then reachable, then locked; by tier inside each band. A
  // hundred rows need an order that answers "what can I do next".
  const band = (r: SkillRow) => (r.learned ? 0 : r.locked ? 2 : 1);
  return rows.sort((a, b) => band(a) - band(b) || a.def.tier - b.def.tier ||
    a.def.name.localeCompare(b.def.name));
}

/** "15 STR · 30 DEX", or "No requirement". */
export function gateText(def: SkillInfo): string {
  const parts: string[] = [];
  for (const { key, label } of GATES) {
    if (def.statGates[key] > 0) parts.push(`${def.statGates[key]} ${label}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'No requirement';
}

/**
 * Why a locked skill is locked, as something the player can act on.
 *
 * "Requires 15 STR" is a fact; "15 STR — you have 10" is a plan.
 */
export function lockText(row: SkillRow): string {
  if (row.missing.length === 0) return '';
  return row.missing
    .map((m) => `${m.need} ${m.attribute} — you have ${m.have}`)
    .join(', ');
}

/** The cost line for a skill's tooltip: stamina, mana and cooldown. */
export function costText(def: SkillInfo): string {
  const parts: string[] = [];
  if (def.staminaCost > 0) parts.push(`${def.staminaCost} stamina`);
  if (def.manaCost > 0) parts.push(`${def.manaCost} mana`);
  if (def.cooldownSec > 0) parts.push(`${def.cooldownSec}s cooldown`);
  return parts.length > 0 ? parts.join(' · ') : 'No cost';
}

// ── Ranks ───────────────────────────────────────────────────────────────────

/**
 * English for one of the server's levellable field keys.
 *
 * The client owns the wording, exactly as it does for a passive's stat lines — the
 * key that arrives is a path into the YAML (`stat_multipliers.str`) and nobody wants
 * to read that in a tooltip.
 */
const FIELD_LABELS: Record<string, string> = {
  weapon_scaling: 'Weapon damage',
  'stat_multipliers.str': 'STR scaling',
  'stat_multipliers.dex': 'DEX scaling',
  'stat_multipliers.int': 'INT scaling',
  'stat_multipliers.end': 'END scaling',
  stamina_cost: 'Stamina cost',
  mana_cost: 'Mana cost',
  cooldown_sec: 'Cooldown',
  cast_time_sec: 'Cast time',
};

/** "Weapon damage", or the raw key if the server grew one this build does not know. */
export function fieldLabel(stat: string): string {
  return FIELD_LABELS[stat] ?? stat;
}

/** One rung, trimmed of trailing zeroes — 1.4, not 1.400. */
function rung(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}

/** "Rank 7 / 20", or "Rank 1" for a skill that goes no further. */
export function rankText(row: SkillRow): string {
  if (row.rank <= 0) return '';
  if (row.maxRank <= 1) return 'Rank 1';
  return `Rank ${row.rank} / ${row.maxRank}`;
}

/** One line of what the next rank changes. */
export interface RankChange {
  label: string;
  from: number;
  to: number;
}

/**
 * What the next rank buys, read straight off the server's ladder.
 *
 * **Never computed here.** The curve shapes live in a YAML this client has no copy
 * of, so the numbers are indexed out of `def.ladder` rather than evaluated — the
 * same rule an item's `scrapValue` follows. Empty at the top rank, and empty for a
 * skill that has no ladder at all.
 */
export function nextRankChanges(row: SkillRow): RankChange[] {
  if (row.rank <= 0 || row.rank >= row.maxRank) return [];

  const out: RankChange[] = [];
  for (const line of row.def.ladder ?? []) {
    // `values[0]` is rank 1, so the current rung is at `rank - 1`.
    const from = line.values[row.rank - 1];
    const to = line.values[row.rank];
    if (from === undefined || to === undefined || from === to) continue;
    out.push({ label: fieldLabel(line.stat), from, to });
  }
  return out;
}

/** "Weapon damage 1.4 → 1.82 · Stamina cost 15 → 13". */
export function nextRankText(row: SkillRow): string {
  const changes = nextRankChanges(row);
  if (changes.length === 0) return '';
  return changes.map((c) => `${c.label} ${rung(c.from)} → ${rung(c.to)}`).join(' · ');
}

/**
 * A weapon family, as the panel says it.
 *
 * The wire's vocabulary is `SWORD_1H`; a requirement line reading *Requires: SWORD_1H*
 * is the server's spelling leaking into the HUD, which is the same fault an unformatted
 * affix key would be. **A `Record` rather than a `switch`, so TypeScript refuses an
 * incomplete map** — an eleventh weapon family added to the wire without a name here is
 * a compile error rather than a skill whose requirement renders blank.
 */
const WEAPON_FAMILY_NAMES: Record<WeaponClass, string> = {
  SWORD_1H: 'One-handed sword',
  SWORD_2H: 'Two-handed sword',
  AXE_1H: 'One-handed axe',
  AXE_2H: 'Two-handed axe',
  MACE_1H: 'One-handed mace',
  MACE_2H: 'Two-handed mace',
  DAGGER: 'Dagger',
  WAND: 'Wand',
  STAFF: 'Staff',
  BOW: 'Bow',
};

/** One weapon family's name, or the wire's own spelling if it is one we do not know. */
export function weaponFamilyName(family: WeaponClass): string {
  return WEAPON_FAMILY_NAMES[family] ?? family;
}

/**
 * What a skill requires in the main hand, as a sentence — or `''` for the 97 that
 * require nothing.
 *
 * Empty rather than "Any weapon": a line on every row saying a skill has no requirement
 * is a hundred lines of noise to mark three exceptions, and the panel's whole problem is
 * that a hundred skills is already too many to read.
 *
 * "or" rather than "and" for a list, because the requirement is satisfied by any one of
 * them — the wrong conjunction here would describe a rule nothing implements.
 */
export function weaponRequirementText(def: Pick<SkillInfo, 'weaponTypes'>): string {
  const families = def.weaponTypes ?? [];
  if (families.length === 0) return '';
  return `Requires ${families.map(weaponFamilyName).join(' or ')}`;
}

/**
 * Whether the weapon in the player's main hand satisfies a skill.
 *
 * **Advisory only.** The server checks the same thing on every cast and its answer is the
 * one that counts; this exists so the panel can grey the row before the player finds out
 * by pressing a key. Mirrored by hand across the language line, which is why
 * `skills.test.ts` walks every family rather than the two cases that came to mind.
 */
export function weaponMet(
  def: Pick<SkillInfo, 'weaponTypes'>,
  mainHand: WeaponClass | undefined,
): boolean {
  const families = def.weaponTypes ?? [];
  if (families.length === 0) return true;
  return mainHand !== undefined && families.includes(mainHand);
}

/**
 * Why a rank cannot be bought, in English.
 *
 * A fifth wording function beside `refusalText`, `beltRefusalText` and
 * `dodgeRefusalText`, for the same reason there is a fourth: `GATE` and
 * `NOT_LEARNED` mean the same thing in both vocabularies and need different
 * sentences. "Your attributes no longer meet that skill's requirement" is right for
 * a cast that failed; here the player is deciding where to spend, and the sentence
 * has to be about the spending.
 *
 * `MAX_RANK` is the one that carries real information: for the fourteen skills the
 * simulation reads nothing from, it is not "you are finished" but "there was never
 * anything here", and a player who cannot tell those apart will keep clicking.
 */
export function rankRefusalText(reason: RefusalReason | undefined, row: SkillRow): string {
  switch (reason) {
    case undefined:
      return '';
    case 'NOT_LEARNED':
      return 'Learn it first.';
    case 'MAX_RANK':
      return row.maxRank <= 1
        ? 'Nothing to level — none of this skill’s numbers are read by the simulation yet.'
        : `Fully levelled at rank ${row.maxRank}.`;
    case 'GATE':
      return 'Meet its attribute requirement before spending on it.';
    case 'NO_POINTS':
      return 'No skill points. Levelling grants one each.';
    default:
      return 'That rank cannot be bought right now.';
  }
}

/**
 * Grouping, tagging and searching the hundred skills.
 *
 * ── Why the panel needed this ────────────────────────────────────────────────
 *
 * A hundred skills in one flat list is a list nobody reads. The panel drew them in
 * catalogue order, which is the order they happen to be written in `skills.yaml` — so
 * finding "the fire spell I can cast now" meant scrolling past ninety others, and the
 * question a player actually asks (*what is available to me?*) had no answer on screen at
 * all.
 *
 * ── Every tag comes from a field ─────────────────────────────────────────────
 *
 * **Nothing here invents content.** A tag is a fact already on `SkillInfo` — the attribute
 * that gates it, what it scales off, whether it costs mana or stamina, whether it has a
 * cooldown, whether it commits you to a cast. Inventing a taxonomy ("fire", "melee",
 * "support") would mean a hundred hand-written entries drifting away from the YAML that
 * actually decides what a skill does, and the drift would be invisible: a mislabelled
 * skill still casts correctly.
 *
 * The one judgement is which attribute *names the group* for a skill gated on two, and it
 * is stated rather than hidden — see `skillFamily`.
 */

/** The attribute family a skill belongs to, which is what the panel groups by. */
export type SkillFamily = 'STR' | 'DEX' | 'INT' | 'END' | 'OPEN';

export const SKILL_FAMILIES: readonly SkillFamily[] = ['STR', 'DEX', 'INT', 'END', 'OPEN'];

export const FAMILY_LABELS: Record<SkillFamily, string> = {
  STR: 'Strength',
  DEX: 'Dexterity',
  INT: 'Intelligence',
  END: 'Endurance',
  OPEN: 'Open to all',
};

/**
 * Which family a skill is filed under: **its highest gate**.
 *
 * A skill gated on 40 STR and 15 DEX is a strength skill that also wants some dexterity,
 * and filing it under both would put it in two places in a panel whose whole purpose is
 * that each skill is in one findable place. The *highest* requirement is the one that
 * decides whether a build can reach it, so it is the one that names the group.
 *
 * Ties break in `STR, DEX, INT, END` order — arbitrary, and it has to be *some* order for
 * the grouping to be stable. A skill gated on nothing is `OPEN`, which is a real category
 * rather than a fallback: those are the skills any build can take.
 */
export function skillFamily(def: SkillInfo): SkillFamily {
  const gates: [SkillFamily, number][] = [
    ['STR', def.statGates.str],
    ['DEX', def.statGates.dex],
    ['INT', def.statGates.int],
    ['END', def.statGates.end],
  ];
  let best: SkillFamily = 'OPEN';
  let highest = 0;
  for (const [family, need] of gates) {
    if (need > highest) {
      highest = need;
      best = family;
    }
  }
  return best;
}

/**
 * A skill's tags, every one of them read off a field.
 *
 * Ordered rather than alphabetical, so a row always reads the same way: what it scales
 * off first (the most build-relevant fact), then what it costs, then what it commits you
 * to. A player scanning a column of rows is comparing the same position each time.
 */
export function skillTags(def: SkillInfo): string[] {
  const tags: string[] = [];

  switch (def.scalingType) {
    case 'WEAPON':
      tags.push('Weapon');
      break;
    case 'ATTRIBUTE':
      tags.push('Attribute');
      break;
    case 'HYBRID':
      tags.push('Hybrid');
      break;
    case 'PASSIVE':
      tags.push('Passive');
      break;
    case 'UTILITY':
      tags.push('Utility');
      break;
  }

  if (def.manaCost > 0) tags.push('Mana');
  if (def.staminaCost > 0) tags.push('Stamina');
  // A skill costing neither is free to press, which is worth saying: it is the whole
  // difference between a filler and a spender.
  if (def.manaCost === 0 && def.staminaCost === 0 && def.scalingType !== 'PASSIVE') {
    tags.push('Free');
  }
  if (def.cooldownSec > 0) tags.push('Cooldown');
  // Absent means instant, which is what 85 of the hundred are — so the tag marks the
  // exception rather than the rule.
  if ((def.castTimeSec ?? 0) > 0) tags.push('Cast');

  return tags;
}

/** Every tag any skill in the catalogue carries, in the order `skillTags` emits them. */
export function tagVocabulary(rows: readonly SkillRow[]): string[] {
  const order = ['Weapon', 'Attribute', 'Hybrid', 'Passive', 'Utility', 'Mana', 'Stamina', 'Free', 'Cooldown', 'Cast'];
  const present = new Set<string>();
  for (const row of rows) for (const tag of skillTags(row.def)) present.add(tag);
  return order.filter((tag) => present.has(tag));
}

/** Which skills the panel is showing. Every field's "unset" means *do not narrow*. */
export interface SkillQuery {
  /** Free text over name, id, category and tags. */
  text: string;
  /** Tags to keep. Empty means all — the chips are off by default. */
  tags: readonly string[];
  /**
   * Availability. `ALL` is everything; `USABLE` is what the player could press or learn
   * right now; `LOCKED` is what they cannot yet reach.
   *
   * Three states rather than two booleans, because "usable and not usable" is a query
   * that means *everything* and a pair of checkboxes invites it.
   */
  availability: 'ALL' | 'USABLE' | 'LOCKED';
  /** Show only what has already been learned. */
  learnedOnly: boolean;
}

export const EMPTY_SKILL_QUERY: SkillQuery = {
  text: '',
  tags: [],
  availability: 'ALL',
  learnedOnly: false,
};

/** Whether a query narrows anything, for the "clear" affordance. */
export function isSkillQueryNarrowed(query: SkillQuery): boolean {
  return (
    query.text.trim() !== '' ||
    query.tags.length > 0 ||
    query.availability !== 'ALL' ||
    query.learnedOnly
  );
}

/**
 * Whether one skill survives a query.
 *
 * `USABLE` means **learned and castable, or learnable right now** — the two things a
 * player can act on this second. A learned skill whose gate has since been broken by
 * taking off an amulet is *not* usable, which is the honest reading and the one the row's
 * own `usable` flag already makes.
 */
export function matchesSkillQuery(row: SkillRow, query: SkillQuery): boolean {
  if (query.learnedOnly && !row.learned) return false;

  if (query.availability === 'USABLE' && !(row.usable || row.learnable)) return false;
  if (query.availability === 'LOCKED' && (row.usable || row.learnable)) return false;

  if (query.tags.length > 0) {
    const tags = skillTags(row.def);
    // **Any**, not all: chips are a widening set within one axis. Requiring every chip
    // would make a second click almost always empty the list, which reads as broken.
    if (!query.tags.some((tag) => tags.includes(tag))) return false;
  }

  const words = query.text.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = [row.def.name, row.def.skillId, row.def.category, ...skillTags(row.def)]
    .join(' ')
    .toLowerCase();
  return words.every((word) => haystack.includes(word));
}

/** One family's skills, for rendering. */
export interface SkillGroup {
  family: SkillFamily;
  label: string;
  rows: SkillRow[];
}

/**
 * The rows to draw, filtered and grouped.
 *
 * **Empty groups are dropped**, because a heading over nothing is noise in a panel whose
 * whole job is to make a hundred skills findable. Within a group the catalogue's own order
 * is kept — it is tier order, which is the order a build progresses through.
 */
export function groupSkills(rows: readonly SkillRow[], query: SkillQuery): SkillGroup[] {
  const kept = rows.filter((row) => matchesSkillQuery(row, query));
  return SKILL_FAMILIES.map((family) => ({
    family,
    label: FAMILY_LABELS[family],
    rows: kept.filter((row) => skillFamily(row.def) === family),
  })).filter((group) => group.rows.length > 0);
}

/** Toggles one tag, so a second click clears it. */
export function toggleSkillTag(current: readonly string[], tag: string): string[] {
  return current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag];
}
